import Cookies from 'js-cookie';
import { googleConsentManager } from './google-consent';
import { posthogConsentManager } from './posthog-consent';

// Define the available consent categories
type ConsentCategory = 'analytics' | 'chat';

// Consent settings type now allows undefined values to indicate "not set"
export type ConsentSettings = Partial<Record<ConsentCategory, boolean>>;

// Interface for consent providers (e.g., Google Analytics)
export interface ConsentProvider {
    handleInitialConsent?: (value: boolean | undefined) => void;
    handleConsentUpdate?: (value: boolean) => void;
}

// Configuration for consent-related cookies.
// You can extend this configuration for additional consent categories or providers.
const COOKIE_CONFIG: Record<ConsentCategory, {
    name: string;
    expiry: number;
    path: string;
    providers: ConsentProvider[];
}> = {
    analytics: {
        name: 'analyticsConsent',
        expiry: 365,
        path: '/',
        providers: [googleConsentManager, posthogConsentManager]
    },
    chat: {
        name: 'hasChatConsent',
        expiry: 365,
        path: '/',
        providers: []
    }
};

export const consentManager = {
    // Get the initial consent state from cookies.
    // For each category, if the cookie is not set, its value remains undefined.
    // The provider is always notified (using false as the default when no cookie exists).
    getInitialState: (): ConsentSettings => {
        if (typeof window === 'undefined') return {};

        const consentSettings: ConsentSettings = {};
        Object.entries(COOKIE_CONFIG).forEach(([category, config]) => {
            const cookieValue = Cookies.get(config.name);
            const isGranted = cookieValue !== undefined ? Boolean(cookieValue) : undefined;
            consentSettings[category as ConsentCategory] = isGranted;
            // Always run the initial consent function for providers.
            config.providers.forEach(p => p.handleInitialConsent?.(isGranted));
        });
        return consentSettings;
    },

    // Update consent settings and notify providers.
    // settings here should have complete boolean values.
    updateState: (settings: Record<ConsentCategory, boolean>) => {
        try {
            Object.entries(settings).forEach(([category, value]) => {
                const config = COOKIE_CONFIG[category as ConsentCategory];
                // Save the consent value to a cookie.
                Cookies.set(config.name, String(value), {
                    expires: config.expiry,
                    path: config.path
                });
                // Notify the providers about the consent update.
                config.providers.forEach(p => p.handleConsentUpdate?.(value));
            });
        } catch (error) {
            console.error('Error updating consent state:', error);
            throw new Error('Failed to update consent settings');
        }
    }
};